import i18next, { type TFunction } from 'i18next';

type TemplateTextField = 'name' | 'description';

type TemplateTextTranslation = {
    language?: string;
    name?: string | null;
    description?: string | null;
    externalDescription?: string | null;
    achievementLevels?: TemplateTextTranslation[] | null;
};

let translationsByTemplateId: Record<string, TemplateTextTranslation[]> = {};

export function setTemplateTranslations(
    templateCategories: Array<{
        id: string;
        translations?: TemplateTextTranslation[] | null;
        quests: Array<{ id: string; translations?: TemplateTextTranslation[] | null }>;
        achievements: Array<{ id: string; translations?: TemplateTextTranslation[] | null }>;
    }>,
): void {
    translationsByTemplateId = Object.fromEntries(
        templateCategories
            .flatMap(category => [
                [category.id, category.translations],
                ...category.quests.map(quest => [quest.id, quest.translations]),
                ...category.achievements.map(achievement => [achievement.id, achievement.translations]),
            ])
            .filter((entry): entry is [string, TemplateTextTranslation[]] => Boolean(entry[1]?.length)),
    );
}

export function registerTemplateTranslations(templateId: string | null | undefined, translations?: TemplateTextTranslation[] | null): void {
    if (!templateId || !translations?.length) return;
    translationsByTemplateId[templateId] = translations;
}

function getTranslation(templateId: string, levelIndex?: number | null): TemplateTextTranslation | undefined {
    const language = i18next.language.toUpperCase();
    const translation = translationsByTemplateId[templateId]?.find(item => item.language === language);
    return levelIndex == null ? translation : translation?.achievementLevels?.[levelIndex];
}

export function translateTemplateText(
    t: TFunction,
    templateId: string | null | undefined,
    field: TemplateTextField,
    fallback: string,
): string {
    if (!templateId) return fallback;
    return getTranslation(templateId)?.[field] ?? t('templateTranslationFallback', { defaultValue: fallback });
}

export function translateTemplateLevelText(
    t: TFunction,
    templateId: string | null | undefined,
    levelIndex: number | null | undefined,
    field: TemplateTextField,
    fallback: string,
): string {
    if (!templateId || levelIndex == null) return fallback;
    return getTranslation(templateId, levelIndex)?.[field] ?? t('templateTranslationFallback', { defaultValue: fallback });
}
